import { useState } from 'react';
import {
  ChakraBox,
  ChakraFlex,
  EmptyListV2,
  FAIconName,
  TableFilter,
} from '@equip.health/ui';
import { DEFAULT_PAGINATION_CONFIG } from '~/lib/constants';
import { getUserListFilterParamsV2 } from '~/lib/util/user.util';
import { useGetAllProvidersV2Query } from '~/store/services/providers';
import UsersTableActionBar from './UsersTableActionBar';
import UsersTable from './UsersTable';
import UserFilters from './UserFilters';
import {
  NO_USERS_FOUND_TEXT,
  NO_USERS_FOUND_SUBTEXT,
} from '~/lib/constants/user.constants';
import { useFlags } from 'launchdarkly-react-client-sdk';

const TOP_LEVEL_ID = 'admin-users';

const AdminUsers = () => {
  const { providerTypePkTransition: isProviderTypePkTransitionEnabled } =
    useFlags<LDFlags>();
  const [sortByColumn, setSortByColumn] = useState<string>('name');
  const [tableFilters, setTableFilters] = useState<TableFilter[]>([]);

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    DEFAULT_PAGINATION_CONFIG.pageSize,
  );

  const {
    data: users,
    isLoading,
    isFetching,
  } = useGetAllProvidersV2Query({
    'include-out-of-office': true,
    'page-number': pageNumber,
    'page-size': itemsPerPage,
    'sort-by': sortByColumn,
    // this typing override lets us have an accurate API type
    // out of scope of the current change to go back and properly type this helper/upstream usage
    ...(getUserListFilterParamsV2(
      tableFilters,
      isProviderTypePkTransitionEnabled,
    ) as UserListFilterParamType & {
      status?: 'active' | 'inactive';
    }),
  });

  const handleSortByChange = (value: string): void => {
    setSortByColumn(value);
  };

  const handlePageChange = (page: number) => {
    setPageNumber(page);
  };

  const handleItemPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPageNumber(1);
  };

  const handleFiltersChange = (filters: TableFilter[]) => {
    setTableFilters(filters);
    setPageNumber(1);
  };

  const handleStartOver = () => {
    setTableFilters([]);
    setPageNumber(1);
  };

  const hasUsers = !!users?.count;

  return (
    <ChakraFlex
      direction="column"
      gap="20px"
      padding="30px 0 30px 6px"
      width="100%"
    >
      <UsersTableActionBar
        isDataLoading={isLoading || isFetching}
        onSortByChange={handleSortByChange}
        sortBy={sortByColumn}
        userCount={users?.count ?? 0}
      />
      <UserFilters
        id="admin-users"
        onFiltersChange={handleFiltersChange}
        selectedFilters={tableFilters}
      />

      {!isLoading &&
        (hasUsers ? (
          <UsersTable
            handleItemPerPageChange={handleItemPerPageChange}
            handlePageChange={handlePageChange}
            id={TOP_LEVEL_ID}
            isLoading={isLoading || isFetching}
            pageNumber={pageNumber}
            totalCount={users?.count ?? 0}
            users={users?.data ?? []}
          />
        ) : (
          <ChakraBox marginTop="60px">
            <EmptyListV2
              buttonProps={{
                leftIcon: FAIconName.redoRegular,
                onClick: handleStartOver,
                text: 'Start over',
              }}
              id={TOP_LEVEL_ID}
              primaryText={NO_USERS_FOUND_TEXT}
              secondaryText={NO_USERS_FOUND_SUBTEXT}
            />
          </ChakraBox>
        ))}
    </ChakraFlex>
  );
};

export default AdminUsers;
